import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Building2, Plus, ExternalLink, Mail, Phone, Users, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

export function AdminOrganizers() {
  const [organizers, setOrganizers] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [allotModalOrg, setAllotModalOrg] = useState(null);

  // Form fields
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [description, setDescription] = useState("");
  const [adminName, setAdminName] = useState("");
  const [adminPassword, setAdminPassword] = useState("");

  const loadData = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/organizers").then((r) => setOrganizers(r.data)),
      api.get("/admin/events").then((r) => setEvents(r.data)),
    ]).finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = {
        name,
        slug: slug.toLowerCase().replace(/[^a-z0-9-]/g, "-"),
        contact_email: contactEmail,
        contact_phone: contactPhone || null,
        description: description || null,
        admin_name: adminName || name,
        admin_password: adminPassword,
      };

      await api.post("/admin/organizers", payload);
      toast.success(`Organizer ${name} created successfully!`);
      setOpenCreate(false);
      setName("");
      setSlug("");
      setContactEmail("");
      setContactPhone("");
      setDescription("");
      setAdminName("");
      setAdminPassword("");
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create organizer");
    } finally {
      setCreating(false);
    }
  };

  const handleToggleAllotment = async (eventId, isCurrentlyAllotted, targetSlug) => {
    try {
      const newSlug = isCurrentlyAllotted ? "cgc" : targetSlug;
      await api.put(`/admin/events/${eventId}/allot`, { organizer_slug: newSlug });
      toast.success(isCurrentlyAllotted ? "Event unallotted (returned to CGC Direct)" : `Event allotted to ${allotModalOrg?.name}!`);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update allotment");
    }
  };

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <div className="eyebrow">Multi-Tenant Ecosystem</div>
          <h1 className="font-display text-3xl font-extrabold mt-1">Partner Organizers</h1>
          <p className="text-stone-500 text-sm mt-0.5">
            Manage external event organizers, allot events, and configure dedicated portals & gateways.
          </p>
        </div>

        <Dialog open={openCreate} onOpenChange={setOpenCreate}>
          <DialogTrigger asChild>
            <Button className="btn-brand text-xs flex items-center gap-1.5">
              <Plus size={14} /> Add Partner Organizer
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Building2 size={18} /> Register Collaborating Organizer
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleCreate} className="space-y-4 text-xs mt-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Organizer Name *</Label>
                  <Input
                    placeholder="e.g. Offclub Events"
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      if (!slug) {
                        setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, "-"));
                      }
                    }}
                    required
                  />
                </div>
                <div className="space-y-1">
                  <Label>URL Slug * (/events/slug)</Label>
                  <Input
                    placeholder="e.g. offclub"
                    value={slug}
                    onChange={(e) => setSlug(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="space-y-1">
                <Label>About Organizer</Label>
                <Input
                  placeholder="Short bio or genre"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Contact Email *</Label>
                  <Input
                    type="email"
                    placeholder="team@offclub.in"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-1">
                  <Label>Phone Number</Label>
                  <Input
                    placeholder="+91 98765 43210"
                    value={contactPhone}
                    onChange={(e) => setContactPhone(e.target.value)}
                  />
                </div>
              </div>

              <div className="border-t border-border pt-3 space-y-3">
                <h4 className="font-semibold text-stone-900 dark:text-stone-100 flex items-center gap-1">
                  <ShieldCheck size={13} /> Dedicated Organizer Admin Account
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>Admin Contact Name</Label>
                    <Input
                      placeholder="e.g. Offclub Admin"
                      value={adminName}
                      onChange={(e) => setAdminName(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>Admin Initial Password *</Label>
                    <Input
                      type="password"
                      placeholder="Min 6 characters"
                      value={adminPassword}
                      onChange={(e) => setAdminPassword(e.target.value)}
                      required
                    />
                  </div>
                </div>
                <p className="text-[11px] text-stone-500">
                  The organizer can log in at <code>/admin/login</code> with this email and password to access their dedicated portal.
                </p>
              </div>

              <Button type="submit" disabled={creating} className="w-full btn-brand py-2 text-xs">
                {creating ? "Creating Organizer…" : "Create Partner Organizer"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Organizers Table */}
      <div className="cgc-card overflow-hidden">
        <table className="w-full text-xs text-left">
          <thead className="bg-stone-100 dark:bg-stone-900 text-stone-600 dark:text-stone-300 font-semibold border-b border-border">
            <tr>
              <th className="p-3.5">Organizer</th>
              <th className="p-3.5">Allotted Events</th>
              <th className="p-3.5">Public Hub</th>
              <th className="p-3.5">Contact</th>
              <th className="p-3.5">Gateway</th>
              <th className="p-3.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {organizers.map((o) => {
              const allottedEvents = events.filter((e) => e.organizer_slug === o.slug);
              return (
                <tr key={o.id} className="hover:bg-secondary/40 transition-colors">
                  <td className="p-3.5">
                    <div className="flex items-center gap-3">
                      {o.logo_url ? (
                        <img src={o.logo_url} alt="" className="w-8 h-8 rounded-lg object-cover" />
                      ) : (
                        <div className="w-8 h-8 rounded-lg bg-orange-600/10 text-orange-600 font-bold flex items-center justify-center font-display">
                          {o.name.slice(0, 2).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <div className="font-semibold text-stone-900 dark:text-stone-100">{o.name}</div>
                        <div className="text-[11px] text-stone-400">{o.tagline || o.slug}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-3.5">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold px-2 py-0.5 rounded-full bg-orange-100 dark:bg-orange-950/70 text-orange-800 dark:text-orange-300 border border-orange-200 text-[11px]">
                        {allottedEvents.length} {allottedEvents.length === 1 ? "event" : "events"}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-[11px] h-6 px-2 text-stone-600 dark:text-stone-300 hover:text-orange-600"
                        onClick={() => setAllotModalOrg(o)}
                      >
                        Allot Events →
                      </Button>
                    </div>
                  </td>
                  <td className="p-3.5">
                    <a
                      href={`/events/${o.slug}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 font-mono text-orange-600 hover:underline"
                    >
                      /events/{o.slug} <ExternalLink size={11} />
                    </a>
                  </td>
                  <td className="p-3.5 text-stone-600 dark:text-stone-400 space-y-0.5">
                    <div>{o.contact_email}</div>
                    {o.contact_phone && <div className="text-[11px] text-stone-400">{o.contact_phone}</div>}
                  </td>
                  <td className="p-3.5">
                    {o.custom_razorpay_enabled ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400 border border-emerald-200">
                        Independent Razorpay
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-stone-100 text-stone-600 dark:bg-stone-800 dark:text-stone-400">
                        Platform Shared
                      </span>
                    )}
                  </td>
                  <td className="p-3.5 text-right space-x-1">
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs h-7"
                      onClick={() => setAllotModalOrg(o)}
                    >
                      Allot Events
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs h-7"
                      onClick={() => window.open(`/events/${o.slug}`, "_blank")}
                    >
                      View Hub ↗
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Allot Events Modal */}
      {allotModalOrg && (
        <Dialog open={true} onOpenChange={(open) => { if (!open) setAllotModalOrg(null); }}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Building2 size={18} className="text-orange-600" />
                Allot Events to {allotModalOrg.name} ({allotModalOrg.slug})
              </DialogTitle>
            </DialogHeader>
            <p className="text-xs text-stone-500">
              Select which events belong to this partner organizer. Allotting an event grants the partner access to manage tickets, scan QR codes, and see all attendee orders in their dashboard.
            </p>

            <div className="space-y-2 mt-3">
              {events.map((ev) => {
                const isAllotted = ev.organizer_slug === allotModalOrg.slug;
                const otherPartner = ev.organizer_slug && ev.organizer_slug !== "cgc" && !isAllotted;
                return (
                  <div
                    key={ev.id}
                    className={`p-3 rounded-lg border text-xs flex items-center justify-between gap-3 transition-colors ${
                      isAllotted
                        ? "border-orange-300 bg-orange-50/60 dark:bg-orange-950/20"
                        : "border-border hover:bg-secondary/40"
                    }`}
                  >
                    <div className="space-y-0.5 min-w-0">
                      <div className="font-semibold text-stone-900 dark:text-stone-100 truncate">
                        {ev.title}
                      </div>
                      <div className="text-[11px] text-stone-500 flex items-center gap-2">
                        <span>{ev.category}</span>
                        <span>·</span>
                        <span>{ev.city}</span>
                        <span>·</span>
                        <span>
                          Current:{" "}
                          <strong className={isAllotted ? "text-orange-600" : ""}>
                            {ev.organizer || (ev.organizer_slug ? ev.organizer_slug : "CGC Platform")}
                          </strong>
                        </span>
                      </div>
                    </div>

                    <div>
                      {isAllotted ? (
                        <Button
                          variant="destructive"
                          size="sm"
                          className="text-xs h-7"
                          onClick={() => handleToggleAllotment(ev.id, true, allotModalOrg.slug)}
                        >
                          Remove Allotment
                        </Button>
                      ) : (
                        <Button
                          className="btn-brand text-xs h-7"
                          size="sm"
                          onClick={() => handleToggleAllotment(ev.id, false, allotModalOrg.slug)}
                        >
                          {otherPartner ? `Re-allot to ${allotModalOrg.slug}` : "Allot to Partner"}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
              {events.length === 0 && (
                <div className="text-center py-8 text-stone-500 text-xs">No events found in platform.</div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

export default AdminOrganizers;
